<template>
  <div class="mss" @focusin="onFocusIn" @focusout="onFocusOut">
    <multiselect
      :id="elementId"
      ref="ms"
      :value="null"
      :options="mergedOptions"
      :loading="loading"
      :internal-search="false"
      :searchable="active"
      :allow-empty="true"
      :disabled="disabled || atLimit"
      :placeholder="atLimit ? `Maximum of ${limit} reached` : placeholder"
      track-by="id"
      :custom-label="labelFor"
      select-label=""
      selected-label=""
      deselect-label=""
      @select="onSelect"
      @search-change="onSearch"
      @open="onOpen"
      @close="onClose"
    >
      <template slot="noResult">
        <span v-if="!loading">No members found.</span>
      </template>
      <template slot="noOptions">Start typing to search...</template>
      <li v-if="loadingMore" slot="afterList" class="multiselect__element">
        <span class="multiselect__option text-muted">Loading more...</span>
      </li>
    </multiselect>

    <!-- Same markup/classes as the legacy @voerro tags-input badge (styled in
         _filter.scss / _badges.scss), one chip per selected member — multi mode can hold
         several. @mousedown.prevent keeps the remove button from taking focus, so the click
         never raises a `focusin` that bubbles to `.mss` and re-opens the search dropdown. -->
    <div v-if="selectedOptions.length" class="tags-input mt-1 d-flex flex-wrap">
      <span class="badge mr-1 mb-1" v-for="o in selectedOptions" :key="o.id">
        <button
          v-if="!disabled"
          type="button"
          class="tags-input-remove"
          aria-label="Remove"
          @mousedown.prevent
          @click.stop.prevent="remove(o.id)"
        />
        <span class="tags-input-label">{{ labelFor(o) }}</span>
      </span>
    </div>
  </div>
</template>

<script>
import Multiselect from '@/components/AppMultiselect'
import membersApi from '@/services/api/members'

// Server-backed replacement for the legacy `<tags-input :existing-tags="{id: 'member_number: name'}">`
// member pickers (horse owner, horse rider/s, event competitor — CAM-1477 part 2). Mirrors
// HorseSearchSelect.vue (same freeze-prevention checklist: searchable only while focused,
// mergedOptions kept as plain data not computed, initial values hydrated from the parent's own
// request instead of a per-instance API call) but generalised to support `multiple` (Rider/s
// allows up to 10). `value` / `input` keep the tags-input shape: an array of id strings.
const MIN_CHARS = 2
const DEBOUNCE_MS = 300

export default {
  name: 'MemberSearchSelect',
  components: { Multiselect },
  props: {
    value: { type: Array, default: () => [] },
    elementId: { type: String, required: true },
    // 'owner' (horse owner/rider pickers) or 'competitor' (event result picker) — selects which
    // legacy role exclusion the backend replicates. See MemberService::pickerOptions().
    context: { type: String, required: true },
    multiple: { type: Boolean, default: false },
    // Cap on selections when `multiple` — null means no cap. Mirrors tags-input's `:limit`.
    limit: { type: Number, default: null },
    // Pre-resolved [{id, label}] for the currently selected member(s), supplied by the parent's
    // main request (e.g. horse details already embeds `owners`/`members`). When every id in
    // `value` is covered here, no /v2/members/options hydration call is fired.
    initialOptions: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Start typing...' },
    disabled: Boolean,
    required: Boolean
  },
  data() {
    return {
      searchOptions: [],
      selectedCache: [],
      // Kept as plain data (rebuilt via watchers), NOT a computed — see HorseSearchSelect.vue
      // for why: an Options-API computed returning a fresh array loses its cache under the
      // global @vue/composition-api shim, handing vue-multiselect a new `:options` ref every
      // render, which locks the tab once there are several of these on one page.
      mergedOptions: [],
      // The searchable <input> only renders while THIS field is focused — see the focus-pinging
      // freeze documented on HorseSearchSelect.vue (a password-manager extension pings every
      // always-searchable multiselect on the page in an infinite loop).
      active: false,
      loading: false,
      loadingMore: false,
      nextCursor: null,
      term: '',
      searchTimer: null,
      scrollEl: null,
      onScroll: null
    }
  },
  computed: {
    selectedIds() {
      return (this.value || [])
        .map(v => Number(v))
        .filter(id => Number.isInteger(id) && id > 0)
    },
    selectedOptions() {
      return this.selectedIds
        .map(id => this.mergedOptions.find(o => o.id === id))
        .filter(Boolean)
    },
    atLimit() {
      return this.multiple && this.limit != null && this.selectedIds.length >= this.limit
    }
  },
  watch: {
    // Watch the resolved id list, not the `value` array prop directly: parents commonly bind
    // `:value="foo || []"`, which hands a fresh `[]` on every render — comparing the joined
    // string avoids re-running hydrateSelected() (and its request) on every parent re-render.
    selectedIds: {
      immediate: true,
      handler(ids, oldIds) {
        if (oldIds && ids.join(',') === oldIds.join(',')) return
        this.hydrateSelected()
        this.rebuildOptions()
      }
    },
    selectedCache() { this.rebuildOptions() },
    searchOptions() { this.rebuildOptions() }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
    this.detachScroll()
  },
  methods: {
    labelFor(o) {
      return o ? o.label : ''
    },
    extract(res) {
      const d = (res && res.data && res.data.data) || {}
      return { items: d.items || [], nextCursor: d.next_cursor || null }
    },
    rebuildOptions() {
      const seen = new Set(this.searchOptions.map(o => o.id))
      this.mergedOptions = [...this.selectedCache.filter(o => !seen.has(o.id)), ...this.searchOptions]
    },
    hydrateSelected() {
      const known = new Set([
        ...this.selectedCache.map(o => o.id),
        ...this.searchOptions.map(o => o.id),
        ...this.initialOptions.map(o => Number(o.id))
      ])
      const missing = this.selectedIds.filter(id => !known.has(id))

      // Always fold in whatever the parent already resolved for currently-selected ids.
      const fromParent = this.initialOptions
        .filter(o => this.selectedIds.includes(Number(o.id)))
        .map(o => ({ ...o, id: Number(o.id) }))
      this.selectedCache = [
        ...this.selectedCache.filter(o => !fromParent.some(p => p.id === o.id)),
        ...fromParent
      ]

      // Reject anything not a positive integer before it reaches the API — a bad id would hit
      // /v2/members/options with an empty `ids` list, which can only answer with an unfiltered
      // whole-table scan.
      const toFetch = missing.filter(id => !fromParent.some(p => p.id === id) && Number.isInteger(id) && id > 0)
      if (toFetch.length === 0) return

      membersApi.options({ context: this.context, ids: toFetch.join(',') })
        .then((res) => {
          const fetched = this.extract(res).items
          const seen = new Set(this.selectedCache.map(o => o.id))
          this.selectedCache = [...this.selectedCache, ...fetched.filter(o => !seen.has(o.id))]
        })
        .catch(() => {})
    },
    onSelect(option) {
      if (!option || option.id == null) return
      if (this.multiple) {
        if (this.atLimit || this.selectedIds.includes(option.id)) {
          this.resetMultiselect()
          return
        }
        this.selectedCache = [...this.selectedCache.filter(o => o.id !== option.id), option]
        this.$emit('input', [...this.value.map(String), String(option.id)])
      } else {
        this.selectedCache = [option]
        this.$emit('input', [String(option.id)])
      }
      // The selection is shown by our own chip(s) below; the multiselect must stay a plain
      // search box. vue-multiselect keeps its pick in its own `internalValue` and only re-syncs
      // it when the `:value` prop changes — and ours is a static `null` — so wipe that internal
      // state after every select.
      this.resetMultiselect()
    },
    resetMultiselect() {
      const ms = this.$refs.ms
      if (!ms) return
      ms.internalValue = []
      ms.search = ''
    },
    remove(id) {
      if (this.disabled) return
      this.$emit('input', this.selectedIds.filter(i => i !== id).map(String))
    },
    onSearch(query) {
      this.term = query || ''
      clearTimeout(this.searchTimer)
      if (this.term.length < MIN_CHARS) {
        this.searchOptions = []
        this.nextCursor = null
        return
      }
      this.searchTimer = setTimeout(() => this.runSearch(), DEBOUNCE_MS)
    },
    runSearch() {
      this.loading = true
      membersApi.options({ context: this.context, search: this.term })
        .then((res) => {
          const { items, nextCursor } = this.extract(res)
          this.searchOptions = items
          this.nextCursor = nextCursor
        })
        .catch(() => { this.searchOptions = [] })
        .finally(() => { this.loading = false })
    },
    loadMore() {
      if (!this.nextCursor || this.loadingMore || this.loading) return
      this.loadingMore = true
      membersApi.options({ context: this.context, search: this.term, cursor: this.nextCursor })
        .then((res) => {
          const { items, nextCursor } = this.extract(res)
          this.searchOptions = [...this.searchOptions, ...items]
          this.nextCursor = nextCursor
        })
        .catch(() => {})
        .finally(() => { this.loadingMore = false })
    },
    onFocusIn(e) {
      if (this.active || this.disabled || this.atLimit) return
      // Focus landing on a chip's remove button (keyboard tab, or a browser that still focuses
      // it despite @mousedown.prevent) must not activate the search.
      if (e && e.target && e.target.closest && e.target.closest('.tags-input')) return
      this.active = true
      this.$nextTick(() => {
        const ms = this.$refs.ms
        if (!ms) return
        if (!ms.isOpen) ms.activate()
        const input = ms.$refs && ms.$refs.search
        if (input && document.activeElement !== input) input.focus()
      })
    },
    onFocusOut(e) {
      if (e && e.relatedTarget && this.$el.contains(e.relatedTarget)) return
      this.active = false
    },
    onOpen() {
      this.$nextTick(this.attachScroll)
    },
    onClose() {
      this.detachScroll()
    },
    // vue-multiselect has no "load more" hook — watch the dropdown's scroll container.
    attachScroll() {
      const root = this.$refs.ms && this.$refs.ms.$el
      const wrapper = root && root.querySelector('.multiselect__content-wrapper')
      if (!wrapper) return
      this.scrollEl = wrapper
      this.onScroll = () => {
        if (wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight < 40) this.loadMore()
      }
      wrapper.addEventListener('scroll', this.onScroll)
    },
    detachScroll() {
      if (this.scrollEl && this.onScroll) this.scrollEl.removeEventListener('scroll', this.onScroll)
      this.scrollEl = null
      this.onScroll = null
    }
  }
}
</script>

<style scoped>
.tags-input-remove {
  padding: 0;
  border: 0;
  background-color: transparent;
  position: relative;
  z-index: 2;
  cursor: pointer;
}

.tags-input .badge {
  max-width: 100%;
  min-width: 0;
  align-items: flex-start;
}

.tags-input-label {
  white-space: normal;
  overflow-wrap: break-word;
  min-width: 0;
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// src/components/Members/MemberSearchSelect.vue